import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, RefreshCw, Trash2, Send, Bot, Rss, Edit2 } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_PROMPT = "Sei un editor sportivo italiano per un canale Telegram di calcio. Riscrivi la notizia in modo coinvolgente, breve (max 150 parole), con tono appassionato e 3-5 hashtag finali. Italiano impeccabile.";
const DAYS = [["Lun",0],["Mar",1],["Mer",2],["Gio",3],["Ven",4],["Sab",5],["Dom",6]];

function ChannelDialog({ open, onOpenChange, editing, feeds, onSaved }) {
  const blank = { name: "", bot_token: "", chat_id: "", description: "",
    ai_prompt: DEFAULT_PROMPT, publishing_rules: "", frequency_minutes: 60, auto_publish: false,
    keywords_include: "", keywords_exclude: "",
    allowed_hours_start: 6, allowed_hours_end: 23, allowed_days: [0,1,2,3,4,5,6],
    cron_expression: "", template: "", approval_mode: "auto", utm_enabled: true, rewrite_title: true };
  const [form, setForm] = useState(blank);
  const [selectedFeeds, setSelectedFeeds] = useState([]);

  useEffect(() => {
    if (editing) {
      setForm({ ...blank, ...editing,
        keywords_include: (editing.keywords_include||[]).join(", "),
        keywords_exclude: (editing.keywords_exclude||[]).join(", "),
        allowed_days: editing.allowed_days || [0,1,2,3,4,5,6] });
      setSelectedFeeds(feeds.filter(f => (f.channel_ids||[]).includes(editing.id)).map(f=>f.id));
    } else { setForm(blank); setSelectedFeeds([]); }
    // eslint-disable-next-line
  }, [editing, open]);

  const toggleFeed = (id) => setSelectedFeeds(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);
  const toggleDay = (d) => setForm(f => ({ ...f, allowed_days: f.allowed_days.includes(d) ? f.allowed_days.filter(x=>x!==d) : [...f.allowed_days, d].sort() }));

  const submit = async () => {
    if (!form.name || !form.bot_token || !form.chat_id) { toast.error("Nome, token e chat ID obbligatori"); return; }
    try {
      const payload = {
        ...form,
        frequency_minutes: parseInt(form.frequency_minutes) || 60,
        allowed_hours_start: parseInt(form.allowed_hours_start) || 0,
        allowed_hours_end: parseInt(form.allowed_hours_end) || 23,
        keywords_include: (form.keywords_include||"").split(",").map(s=>s.trim()).filter(Boolean),
        keywords_exclude: (form.keywords_exclude||"").split(",").map(s=>s.trim()).filter(Boolean),
        cron_expression: form.cron_expression || null,
        template: form.template || null,
      };
      let chId = editing?.id;
      if (editing) { await api.patch(`/telegram/channels/${editing.id}`, payload); }
      else { const r = await api.post("/telegram/channels", payload); chId = r.data.id; }
      for (const f of feeds) {
        const has = (f.channel_ids||[]).includes(chId);
        const should = selectedFeeds.includes(f.id);
        if (has !== should) {
          const newList = should ? [...(f.channel_ids||[]), chId] : (f.channel_ids||[]).filter(x=>x!==chId);
          await api.patch(`/rss/feeds/${f.id}`, { channel_ids: newList });
        }
      }
      toast.success(editing ? "Canale aggiornato" : "Canale creato"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Configura canale" : "Nuovo canale Telegram"}</DialogTitle></DialogHeader>
        <Tabs defaultValue="general">
          <TabsList className="bg-slate-950 border border-slate-800 flex-wrap h-auto">
            <TabsTrigger value="general">Generale</TabsTrigger>
            <TabsTrigger value="ai">Prompt AI</TabsTrigger>
            <TabsTrigger value="feeds">Feed RSS</TabsTrigger>
            <TabsTrigger value="filters">Filtri</TabsTrigger>
            <TabsTrigger value="schedule">Programmazione</TabsTrigger>
            <TabsTrigger value="template">Template</TabsTrigger>
            <TabsTrigger value="rules">Note</TabsTrigger>
          </TabsList>
          <TabsContent value="general" className="space-y-3 mt-4">
            <div><Label>Nome</Label><Input data-testid="ch-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" placeholder="MilanNews24" /></div>
            <div><Label>Bot Token</Label><Input type="password" data-testid="ch-token" value={form.bot_token} onChange={e=>setForm({...form, bot_token:e.target.value})} className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs" /></div>
            <div><Label>Chat ID / @username</Label><Input data-testid="ch-chatid" value={form.chat_id} onChange={e=>setForm({...form, chat_id:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
            <div><Label>Descrizione</Label><Textarea value={form.description||""} onChange={e=>setForm({...form, description:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" rows={2} /></div>
            <div className="flex items-center justify-between p-3 bg-slate-950 border border-slate-800 rounded-md">
              <div><Label>Auto-pubblicazione</Label><p className="text-xs text-slate-500 mt-0.5">Pubblica automaticamente dai feed RSS associati.</p></div>
              <Switch data-testid="ch-auto" checked={!!form.auto_publish} onCheckedChange={v=>setForm({...form, auto_publish:v})} />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-950 border border-slate-800 rounded-md">
              <div><Label>Modalità approvazione</Label><p className="text-xs text-slate-500 mt-0.5">Manuale: gli articoli vengono marcati come "approvati" invece di pubblicati.</p></div>
              <Switch checked={form.approval_mode === "manual"} onCheckedChange={v=>setForm({...form, approval_mode: v ? "manual" : "auto"})} />
            </div>
            <div><Label>Frequenza (min)</Label><Input type="number" value={form.frequency_minutes} onChange={e=>setForm({...form, frequency_minutes:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" min={2} /></div>
          </TabsContent>
          <TabsContent value="ai" className="space-y-3 mt-4">
            <div><Label>Prompt AI personalizzato</Label>
              <Textarea data-testid="ch-ai-prompt" value={form.ai_prompt||""} onChange={e=>setForm({...form, ai_prompt:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" rows={8} placeholder={DEFAULT_PROMPT} />
              <p className="text-xs text-slate-500 mt-1">Usato per riscrivere gli articoli prima di pubblicarli.</p>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-950 border border-slate-800 rounded-md">
              <div><Label>Riscrivi anche il titolo</Label></div>
              <Switch checked={!!form.rewrite_title} onCheckedChange={v=>setForm({...form, rewrite_title:v})} />
            </div>
          </TabsContent>
          <TabsContent value="feeds" className="mt-4">
            <Label className="mb-2 block">Feed RSS associati</Label>
            {!Array.isArray(feeds) || feeds.length === 0 ? <div className="text-sm text-slate-500 p-4 border border-dashed border-slate-800 rounded-md">Nessun feed disponibile. Aggiungilo nella sezione Feed RSS.</div> :
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto scrollbar-thin">
                {feeds.map(f => f && f.id ? (
                  <button key={f.id} type="button" onClick={()=>toggleFeed(f.id)} data-testid={`ch-feed-${f.id}`} className={`text-left px-3 py-2 rounded-md border text-sm ${selectedFeeds.includes(f.id) ? "bg-blue-600/20 border-blue-500/50 text-blue-200" : "bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700"}`}>
                    <div className="font-medium truncate">{f.name || "Senza nome"}</div>
                    <div className="text-[10px] text-slate-500 truncate font-mono">{f.url || ""}</div>
                  </button>
                ) : null)}
              </div>}
            <p className="text-[11px] text-slate-500 mt-2">Seleziona uno o più feed. Gli stessi feed possono essere assegnati a più canali.</p>
          </TabsContent>
          <TabsContent value="filters" className="space-y-3 mt-4">
            <div><Label>Keyword INCLUDE (almeno una deve essere presente)</Label>
              <Input data-testid="ch-include" value={form.keywords_include} onChange={e=>setForm({...form, keywords_include:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" placeholder="milan, ibrahimovic, leao" />
              <p className="text-[11px] text-slate-500 mt-1">Separati da virgola. Vuoto = nessun filtro include.</p></div>
            <div><Label>Keyword EXCLUDE (se presente, scarta)</Label>
              <Input data-testid="ch-exclude" value={form.keywords_exclude} onChange={e=>setForm({...form, keywords_exclude:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" placeholder="scommesse, infortunio grave" />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-950 border border-slate-800 rounded-md">
              <div><Label>UTM tracking</Label><p className="text-xs text-slate-500 mt-0.5">Aggiunge utm_source/medium/campaign ai link nei post.</p></div>
              <Switch checked={!!form.utm_enabled} onCheckedChange={v=>setForm({...form, utm_enabled:v})} />
            </div>
          </TabsContent>
          <TabsContent value="schedule" className="space-y-3 mt-4">
            <div><Label className="text-sm">Modalità semplice</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div><Label className="text-xs text-slate-400">Ora inizio (0-23)</Label><Input type="number" min={0} max={23} value={form.allowed_hours_start} onChange={e=>setForm({...form, allowed_hours_start:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
                <div><Label className="text-xs text-slate-400">Ora fine (0-23)</Label><Input type="number" min={0} max={23} value={form.allowed_hours_end} onChange={e=>setForm({...form, allowed_hours_end:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
              </div>
              <div className="mt-3"><Label className="text-xs text-slate-400">Giorni attivi</Label>
                <div className="flex gap-1 mt-1.5 flex-wrap">
                  {DAYS.map(([lbl, n]) => (
                    <button type="button" key={n} onClick={()=>toggleDay(n)} className={`px-3 py-1.5 rounded-md text-xs font-medium border ${form.allowed_days.includes(n) ? "bg-blue-600/20 border-blue-500/50 text-blue-200" : "bg-slate-950 border-slate-800 text-slate-500"}`}>{lbl}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="pt-3 border-t border-slate-800">
              <Label className="text-sm">Modalità avanzata (cron)</Label>
              <Input data-testid="ch-cron" value={form.cron_expression||""} onChange={e=>setForm({...form, cron_expression:e.target.value})} className="bg-slate-950 border-slate-800 mt-1 font-mono" placeholder="0 6-23 * * 1-5" />
              <p className="text-[11px] text-slate-500 mt-1">Se compilato, sovrascrive la modalità semplice. Formato standard cron (m h dom mon dow).</p>
            </div>
            {editing && (
              <div className="pt-3 border-t border-slate-800">
                <Button type="button" variant="outline" className="w-full border-blue-500/40 text-blue-300 hover:bg-blue-600/10" onClick={async()=>{
                  try {
                    const { data } = await api.post(`/ai/suggest-schedule/${editing.id}`);
                    setForm(f => ({...f, allowed_hours_start: data.start, allowed_hours_end: data.end, allowed_days: data.days}));
                    toast.success(data.reason);
                  } catch { toast.error("Errore AI"); }
                }}>✨ Suggerimento AI orari ottimali</Button>
              </div>
            )}
          </TabsContent>
          <TabsContent value="template" className="space-y-3 mt-4">
            <div className="bg-amber-600/10 border border-amber-500/30 rounded-md p-3 text-xs text-amber-100">
              Consigliato: lascia vuoto questo campo e gestisci i template professionali da Automazioni → Template Telegram. Il link RSS diventa automaticamente un pulsante Telegram, quindi non serve inserire URL nel testo.
            </div>
            <Label>Template di pubblicazione canale</Label>
            <Textarea data-testid="ch-template" value={form.template||""} onChange={e=>setForm({...form, template:e.target.value})} className="bg-slate-950 border-slate-800 mt-1 font-mono text-sm" rows={8}
              placeholder="🔴⚫ <b>{title}</b>

{body}

🔗 {link}
Fonte: {source}" />
            <div className="text-[11px] text-slate-500">Variabili: <code className="text-blue-400">{"{title}"}</code> <code className="text-blue-400">{"{body}"}</code> <code className="text-blue-400">{"{link}"}</code> <code className="text-blue-400">{"{source}"}</code>.</div>
            <div className="mt-3">
              <Label className="text-xs uppercase font-mono text-blue-300">Anteprima live</Label>
              <div className="mt-1 p-4 bg-slate-950 border border-blue-500/30 rounded-md text-sm whitespace-pre-wrap min-h-[100px]"
                dangerouslySetInnerHTML={{__html: (form.template||"Nessun template — verrà inviato solo il testo riscritto dall'AI.")
                  .replace(/{title}/g,"<b>Lukaku show, Napoli batte Roma 3-1</b>")
                  .replace(/{body}/g,"Doppietta di Lukaku e gol di Kvaratskhelia. Mourinho furioso a fine partita...")
                  .replace(/{link}/g,"https://example.com/articolo")
                  .replace(/{source}/g, "Gazzetta dello Sport")}} />
            </div>
          </TabsContent>
          <TabsContent value="rules" className="mt-4">
            <Label>Note interne / regole testuali</Label>
            <Textarea data-testid="ch-rules" value={form.publishing_rules||""} onChange={e=>setForm({...form, publishing_rules:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" rows={6} placeholder="Promemoria per il team..." />
          </TabsContent>
        </Tabs>
        <DialogFooter><Button onClick={submit} data-testid="ch-save" className="bg-blue-600 hover:bg-blue-700">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TelegramChannels() {
  const [channels, setChannels] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testForm, setTestForm] = useState({ bot_token: "", chat_id: "", text: "Test CalcioHub AI 🚀" });

  const load = async () => {
    try {
      const [c, f] = await Promise.all([api.get("/telegram/channels"), api.get("/rss/feeds")]);
      setChannels(c.data || []); setFeeds(f.data || []);
    } catch (e) {
      toast.error("Errore caricamento: " + (e?.response?.data?.detail || e?.message || "servizio non disponibile"));
      setChannels([]); setFeeds([]);
    }
  };
  useEffect(() => { load(); }, []);

  const refresh = async (id) => { try { await api.post(`/telegram/channels/${id}/refresh`); toast.success("Aggiornato"); load(); } catch (e) { toast.error(e?.response?.data?.detail); } };
  const remove = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/telegram/channels/${id}`); load(); };
  const sendTest = async () => {
    try { await api.post("/telegram/test", testForm); toast.success("Messaggio inviato!"); setTestOpen(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const feedsForChannel = (chId) => feeds.filter(f => (f.channel_ids||[]).includes(chId));

  return (
    <div data-testid="telegram-page">
      <PageHeader title="Canali Telegram" subtitle="Ogni canale ha feed RSS, prompt AI e regole di pubblicazione dedicate.">
        <Dialog open={testOpen} onOpenChange={setTestOpen}>
          <DialogTrigger asChild><Button variant="outline" data-testid="test-btn" className="border-slate-700">Test invio</Button></DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
            <DialogHeader><DialogTitle>Test invio messaggio</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Bot Token</Label><Input type="password" value={testForm.bot_token} onChange={e=>setTestForm({...testForm, bot_token:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
              <div><Label>Chat ID</Label><Input value={testForm.chat_id} onChange={e=>setTestForm({...testForm, chat_id:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
              <div><Label>Testo</Label><Textarea value={testForm.text} onChange={e=>setTestForm({...testForm, text:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" rows={3} /></div>
            </div>
            <DialogFooter><Button onClick={sendTest} className="bg-blue-600 hover:bg-blue-700"><Send size={14} className="mr-2" />Invia</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        <Button data-testid="add-channel-btn" onClick={()=>{ setEditing(null); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700"><Plus size={16} className="mr-1" />Nuovo canale</Button>
      </PageHeader>
      <ChannelDialog open={open} onOpenChange={(o)=>{ setOpen(o); if(!o) setEditing(null); }} editing={editing} feeds={feeds} onSaved={load} />
      <PageContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {channels.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-500 border border-dashed border-slate-800 rounded-md">Nessun canale collegato.</div>
          )}
          {channels.map(c => {
            const chFeeds = feedsForChannel(c.id);
            return (
              <div key={c.id} className="bg-slate-900 border border-slate-800 rounded-md p-5 flex flex-col" data-testid={`channel-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-bold text-white truncate">{c.name}</div>
                  <div className="flex gap-1">
                    {c.auto_publish && <span className="text-[10px] px-2 py-0.5 rounded font-mono bg-blue-600/20 text-blue-300 border border-blue-500/30">AUTO</span>}
                    <span className={`text-[10px] px-2 py-0.5 rounded font-mono ${c.active ? "bg-emerald-600/20 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>{c.active ? "ON" : "OFF"}</span>
                  </div>
                </div>
                <div className="text-xs text-slate-500 font-mono mt-1">{c.chat_id}</div>
                {c.description && <div className="text-xs text-slate-400 mt-2 line-clamp-2">{c.description}</div>}

                <div className="mt-3 space-y-2 text-xs flex-1">
                  <div className="flex items-start gap-2">
                    <Rss size={13} className="text-blue-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-slate-400">{chFeeds.length} feed{chFeeds.length === 1 ? "" : "s"}</div>
                      {chFeeds.length > 0 && <div className="text-[10px] text-slate-500 truncate">{chFeeds.map(f=>f.name).join(" · ")}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Bot size={13} className={c.ai_prompt ? "text-emerald-400" : "text-slate-600"} />
                    <span className="text-slate-400">{c.ai_prompt ? "Prompt AI personalizzato" : "Prompt AI standard"}</span>
                  </div>
                  <div className="text-slate-500 font-mono text-[11px]">↻ ogni {c.frequency_minutes} min</div>
                </div>

                <div className="mt-4 flex items-end justify-between pt-3 border-t border-slate-800">
                  <div className="flex gap-4">
                    <div><div className="text-2xl font-extrabold text-blue-400">{c.subscribers}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">iscritti</div></div>
                    <div><div className="text-2xl font-extrabold text-emerald-400">{c.total_published || 0}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">pubbl.</div></div>
                    <div><div className="text-2xl font-extrabold text-sky-400">{c.total_clicks || 0}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">click</div></div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={()=>refresh(c.id)} data-testid={`refresh-${c.id}`} className="h-8 text-blue-400"><RefreshCw size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={()=>{ setEditing(c); setOpen(true); }} data-testid={`edit-ch-${c.id}`} className="h-8"><Edit2 size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={()=>remove(c.id)} data-testid={`delete-ch-${c.id}`} className="h-8 text-red-400"><Trash2 size={14} /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </PageContent>
    </div>
  );
}
